import { Platform } from "react-native";

import { palette, radius as brandRadius, space as brandSpace, cardShadow } from "@tabletop/design";

/**
 * How the app looks (WI-045, WI-046, ADR-0029).
 *
 * WI-045 made the values consistent and WI-046 gave them a point of view:
 * paper and felt rather than dashboard, because board games are warm physical
 * objects and the greys were all blue, which reads as software.
 *
 * **The brand primitives now come from `packages/design`** rather than being
 * declared here. This file used to be the source of truth for both clients,
 * and the web took its scale wholesale — which is how the site ended up sized
 * for a phone, with a largest space step of 24px on a 1440px page (ADR-0029).
 *
 * What stays here is what is genuinely mobile's: the type ramp for a screen
 * read at fourteen inches, the `Platform.select()` shadow, and `HIT_SLOP`.
 * None of it is expressible on the web, and none of the web's is expressible
 * here.
 *
 * The rendered result is unchanged. Every value below is the value it was.
 */

/** The palette, shared with the site. Sixteen roles, from the pack (WI-085). */
export const colour = palette;

/** A 4pt grid. Four steps, because a fifth gets used at random. */
export const space = brandSpace;

/**
 * `--spp-radius-die` is 9 and `--spp-radius-icon` is 20 (WI-085). The die
 * radius is the one the interface repeats.
 */
export const radius = brandRadius;

/**
 * Five sizes, each with a line height. **Mobile's ramp, and only mobile's.**
 *
 * The line heights are the single biggest change WI-046 made. Every previous
 * style left it to the platform default, which is why dense text looked
 * cramped and headings looked adrift -- the most common reason an interface
 * reads as unfinished rather than plain.
 *
 * The web has its own ramp in `apps/web/tokens.config.mjs`. A 15px body is
 * right at arm's length on a phone and small on a laptop, and no single set of
 * numbers is right for both (ADR-0029).
 */
export const type = {
  display: { fontSize: 26, fontWeight: "700", lineHeight: 31, letterSpacing: -0.4 },
  title: { fontSize: 17, fontWeight: "600", lineHeight: 23, letterSpacing: -0.2 },
  body: { fontSize: 15, fontWeight: "400", lineHeight: 22 },
  meta: { fontSize: 13, fontWeight: "400", lineHeight: 18 },
  label: {
    fontSize: 11,
    fontWeight: "700",
    lineHeight: 14,
    textTransform: "uppercase",
    letterSpacing: 0.8,
  },
} as const;

/**
 * A card sits on the page rather than being drawn on it.
 *
 * Deliberately faint. A heavy shadow on every card is the other way to look
 * unfinished. `elevation` is Android-only while the shadow properties are what
 * react-native-web turns into a box-shadow, so both are set -- which is
 * exactly why the parts live in `packages/design` and the assembly lives here.
 */
export const elevation = Platform.select({
  default: {
    shadowColor: cardShadow.colour,
    shadowOpacity: cardShadow.opacity,
    shadowRadius: cardShadow.blur,
    shadowOffset: { width: 0, height: cardShadow.offsetY },
    elevation: cardShadow.elevation,
  },
});

/** A control you can hit with a thumb. */
export const HIT_SLOP = { top: 8, bottom: 8, left: 8, right: 8 } as const;
